/**
 * Minimal, type-safe accessor for window.Clerk. Used by authFetch (session
 * refresh on 401), preview-session-keepalive (proactive touch), and the
 * anonymous-canvas transport guard (signed-in detection).
 */

import { isAnonymousSurfaceActive } from "@/lib/clerk/anonymousSurface";

export interface MinimalClerkSession {
  touch?: () => Promise<unknown>;
}

export interface MinimalClerk {
  session?: MinimalClerkSession | null;
  /** clerk-js sets this to `true` once initialization finishes. */
  loaded?: boolean;
}

export function readClerk(): MinimalClerk | undefined {
  return (window as unknown as { Clerk?: MinimalClerk }).Clerk;
}

/**
 * Whether this browser has (or is still loading) a signed-in Clerk client.
 * Shared by authFetch and the anonymous-canvas transport guard so their
 * signed-in judgment stays identical.
 *
 * Precedence, most authoritative first:
 * 1. An active `session` → signed in.
 * 2. Clerk loaded, no session, AND on the anonymous canvas → signed out. Only
 *    that surface may override the cookie, because its visitor has no session
 *    by construction (see `anonymousSurface.ts`); a stale `__client_uat` there
 *    is not evidence of a signed-in client.
 * 3. Otherwise → the `__client_uat` cookie (optionally suffixed per publishable
 *    key). A loaded, sessionless client with a non-zero cookie OFF the
 *    anonymous canvas is an EXPIRED signed-in session and must still be treated
 *    as signed in so it gets the session-expired redirect. A non-zero value
 *    means someone was/is signed in.
 */
export function hasSignedInClerkClient(): boolean {
  if (typeof window === "undefined") return false;
  const clerk = readClerk();
  if (clerk?.session) return true;
  if (clerk?.loaded === true && isAnonymousSurfaceActive()) return false;
  return document.cookie.split(";").some((part) => {
    const [rawName, rawValue] = part.split("=");
    const name = rawName?.trim();
    const value = rawValue?.trim();
    if (!name?.startsWith("__client_uat")) return false;
    return !!value && value !== "0";
  });
}

const CLERK_LOAD_POLL_MS = 50;
let clerkLoadedPromise: Promise<boolean> | null = null;

/**
 * Resolve once `window.Clerk.loaded` is `true`, or `false` after `timeoutMs`.
 *
 * Used to break the loading-phase ambiguity: a `__client_uat` cookie is only a
 * hint that a client MAY be signed in; the loaded Clerk client is the
 * authoritative signal. Concurrent callers share one poll so a batch of 401s
 * waits once.
 */
export function waitForClerkLoaded(timeoutMs = 3000): Promise<boolean> {
  if (readClerk()?.loaded === true) return Promise.resolve(true);
  if (clerkLoadedPromise) return clerkLoadedPromise;

  clerkLoadedPromise = new Promise<boolean>((resolve) => {
    const start = Date.now();
    const poll = () => {
      if (readClerk()?.loaded === true) return resolve(true);
      if (Date.now() - start >= timeoutMs) return resolve(false);
      setTimeout(poll, CLERK_LOAD_POLL_MS);
    };
    poll();
  });

  return clerkLoadedPromise.finally(() => {
    clerkLoadedPromise = null;
  });
}

/** Reset the shared load waiter — test-only. */
export function _resetClerkLoadedWaiterForTesting() {
  clerkLoadedPromise = null;
}
